import { Component, OnInit, OnDestroy } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { MiniGameService } from '../../../_services/mini-game.service';
import { MiniGameSession, MysteryBoxState, BoxModifier, BoxCategory, BOX_MODIFIER_CATEGORY, MiniGameStudent } from '../../../interfaces/mini-game.interface';
import { GameQuestion } from '../../../interfaces/question.interface';
import { GlobalService } from 'src/app/_services/global.service';
import { GameAudioService } from '../../../_services/game-audio.service';

type BoxPhase = 'pick' | 'student-show' | 'reveal' | 'question' | 'result';

@Component({
  selector: 'app-mystery-box-game',
  templateUrl: './mystery-box-game.component.html',
  styleUrls: ['./mystery-box-game.component.scss']
})
export class MysteryBoxGameComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  currLang: any = 'en';

  session: MiniGameSession | null = null;
  currentQuestion: GameQuestion | null = null;
  selectedOption: 'A' | 'B' | 'C' | 'D' | null = null;
  internalPhase: BoxPhase = 'pick';
  lastResult: { isCorrect: boolean; xpEarned: number } | null = null;
  currentBoxIndex: number | null = null;
  timerValue = 0;
  currentStudent: MiniGameStudent | null = null;
  private studentIndex = 0;
  showInfo = false;

  BoxModifier = BoxModifier;

  private correctSound = new Audio('assets/game-mode/sound-effects/correctSoundEffect.mp3');
  private buzzerSound = new Audio('assets/game-mode/sound-effects/buzzerSoundEffect.mp3');
  private bonusSound = new Audio('assets/game-mode/sound-effects/crowdCheeringSoundEffect.mp3');
  private bamboozleSound = new Audio('assets/game-mode/sound-effects/wahWahSoundEffect.mp3');
  private pendingTimeouts: any[] = [];
  private destroyed = false;

  get state(): MysteryBoxState | null {
    return this.session?.gameState as MysteryBoxState || null;
  }

  constructor(
    private miniGameService: MiniGameService,
    private globalService: GlobalService,
    private gameAudioService: GameAudioService
  ) {}

  ngOnInit(): void {
    this.globalService.getLang()
      .pipe(takeUntil(this.destroy$))
      .subscribe(lang => this.currLang = lang);

    this.miniGameService.session$
      .pipe(takeUntil(this.destroy$))
      .subscribe(session => {
        this.session = session;
        if (session) {
          this.currentQuestion = session.questions[session.currentQuestionIndex] || null;
        }
      });

    this.miniGameService.lastResult$
      .pipe(takeUntil(this.destroy$))
      .subscribe(result => {
        this.lastResult = result;
        if (result) {
          if (result.isCorrect) {
            this.gameAudioService.playExisting(this.correctSound);
          } else {
            this.gameAudioService.playExisting(this.buzzerSound);
          }
        }
      });

    this.miniGameService.timerValue$
      .pipe(takeUntil(this.destroy$))
      .subscribe(val => this.timerValue = val);

    this.miniGameService.timerExpired$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        if (this.internalPhase === 'question') {
          this.handleTimerExpired();
        }
      });
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.pendingTimeouts.forEach(t => clearTimeout(t));
    this.pendingTimeouts = [];
    this.gameAudioService.stop(this.correctSound);
    this.gameAudioService.stop(this.buzzerSound);
    this.gameAudioService.stop(this.bonusSound);
    this.gameAudioService.stop(this.bamboozleSound);
    this.gameAudioService.stopCountdownAudio();
    this.destroy$.next();
    this.destroy$.complete();
    this.miniGameService.stopTimer();
  }

  private safeTimeout(fn: () => void, ms: number): void {
    const id = setTimeout(() => {
      if (!this.destroyed) fn();
    }, ms);
    this.pendingTimeouts.push(id);
  }

  pickBox(index: number): void {
    if (!this.state || this.state.boxes[index].isUsed) return;

    this.assignNextStudent();
    this.currentBoxIndex = index;
    this.internalPhase = 'student-show';

    // Show student spotlight, then reveal the box modifier
    this.safeTimeout(() => {
      const box = this.state!.boxes[index];
      box.isRevealed = true;
      this.state!.currentModifier = box.modifier;
      this.state!.modifierRevealed = true;
      this.internalPhase = 'reveal';

      // Play card reveal sound and match reveal duration to sound length
      const category = this.getModifierCategory(box.modifier);
      let revealDuration: number;
      if (category === 'bonus') {
        this.gameAudioService.playExisting(this.bonusSound);
        revealDuration = 4000;
      } else {
        this.gameAudioService.playExisting(this.bamboozleSound);
        revealDuration = 5000;
      }

      this.safeTimeout(() => {
        box.isUsed = true;
        this.internalPhase = 'question';
        this.selectedOption = null;

        // Apply time modifiers
        let timeLimit = 30;
        if (box.modifier === BoxModifier.EXTRA_TIME) timeLimit = 45;
        else if (box.modifier === BoxModifier.HALF_TIME) timeLimit = 15;
        else if (box.modifier === BoxModifier.FREEZE) timeLimit = 10;
        this.miniGameService.startTimer(timeLimit);
        this.gameAudioService.startCountdownAudio(this.miniGameService.timerValue$);
      }, revealDuration);
    }, 3500);
  }

  selectOption(optionId: 'A' | 'B' | 'C' | 'D'): void {
    this.selectedOption = optionId;
  }

  submitAnswer(): void {
    if (!this.selectedOption) return;
    this.gameAudioService.stopCountdownAudio();
    this.miniGameService.submitAnswer(this.selectedOption, {
      studentId: this.currentStudent?.id
    });
    this.showResultAndAdvance();
  }

  private handleTimerExpired(): void {
    this.gameAudioService.stopCountdownAudio();
    // Pick a guaranteed wrong answer
    const wrongOption = this.getWrongOption();
    this.selectedOption = wrongOption;
    this.miniGameService.submitAnswer(wrongOption, {
      studentId: this.currentStudent?.id
    });
    this.showResultAndAdvance();
  }

  private getWrongOption(): 'A' | 'B' | 'C' | 'D' {
    if (!this.currentQuestion) return 'A';
    const correct = this.currentQuestion.correctOptionId;
    const options: ('A' | 'B' | 'C' | 'D')[] = ['A', 'B', 'C', 'D'];
    return options.find(o => o !== correct) || 'A';
  }

  private showResultAndAdvance(): void {
    this.internalPhase = 'result';

    this.safeTimeout(() => {
      this.miniGameService.nextQuestion();
      this.internalPhase = 'pick';
      this.selectedOption = null;
      this.currentBoxIndex = null;
      if (this.session) {
        this.currentQuestion = this.session.questions[this.session.currentQuestionIndex] || null;
      }
    }, 2000);
  }

  getQuestionNum(): number {
    return (this.session?.currentQuestionIndex || 0) + 1;
  }

  getTotalQuestions(): number {
    return this.session?.questions.length || 7;
  }

  getModifierCategory(modifier: BoxModifier): BoxCategory {
    return BOX_MODIFIER_CATEGORY[modifier];
  }

  getModifierName(modifier: BoxModifier): string {
    const names: Record<string, { en: string; ar: string }> = {
      // Bonus
      [BoxModifier.DOUBLE_XP]: { en: 'Double XP!', ar: 'نقاط مضاعفة!' },
      [BoxModifier.TRIPLE_XP]: { en: 'Triple XP!', ar: 'نقاط ثلاثية!' },
      [BoxModifier.FREE_PASS]: { en: 'Free Pass!', ar: 'تمريرة مجانية!' },
      [BoxModifier.EXTRA_TIME]: { en: 'Extra Time!', ar: 'وقت إضافي!' },
      [BoxModifier.SHIELD]: { en: 'Shield!', ar: 'درع الحماية!' },
      // Bamboozle
      [BoxModifier.HALF_TIME]: { en: 'Half Time!', ar: 'نصف الوقت!' },
      [BoxModifier.FREEZE]: { en: 'Brain Freeze!', ar: 'تجمد الدماغ!' },
      [BoxModifier.STEAL]: { en: 'Steal or Lose!', ar: 'اسرق أو اخسر!' },
      [BoxModifier.MINUS_SCORE]: { en: 'Score Drain!', ar: 'استنزاف النقاط!' },
      [BoxModifier.REVERSE]: { en: 'Reverse!', ar: 'عكس!' }
    };
    return this.currLang === 'ar' ? names[modifier]?.ar : names[modifier]?.en;
  }

  getModifierDescription(modifier: BoxModifier): string {
    const desc: Record<string, { en: string; ar: string }> = {
      [BoxModifier.DOUBLE_XP]: { en: 'Earn 2x XP this round', ar: 'اكسب ضعف النقاط' },
      [BoxModifier.TRIPLE_XP]: { en: 'Earn 3x XP this round!', ar: 'اكسب ثلاثة أضعاف النقاط!' },
      [BoxModifier.FREE_PASS]: { en: 'Get 5 XP even if wrong', ar: 'احصل على 5 نقاط حتى لو أخطأت' },
      [BoxModifier.EXTRA_TIME]: { en: '45 seconds to answer', ar: '45 ثانية للإجابة' },
      [BoxModifier.SHIELD]: { en: 'No penalty if wrong', ar: 'لا خسارة إذا أخطأت' },
      [BoxModifier.HALF_TIME]: { en: 'Only 15 seconds!', ar: '15 ثانية فقط!' },
      [BoxModifier.FREEZE]: { en: 'Only 10 seconds!', ar: '10 ثوان فقط!' },
      [BoxModifier.STEAL]: { en: '2x if right, -10 if wrong', ar: 'ضعف إذا صح، -10 إذا خطأ' },
      [BoxModifier.MINUS_SCORE]: { en: '-15 XP penalty!', ar: 'خصم 15 نقطة!' },
      [BoxModifier.REVERSE]: { en: 'Right = -5, Wrong = +5!', ar: 'صح = -5، خطأ = +5!' }
    };
    return this.currLang === 'ar' ? desc[modifier]?.ar : desc[modifier]?.en;
  }

  getModifierIcon(modifier: BoxModifier): string {
    const icons: Record<string, string> = {
      // Bonus
      [BoxModifier.DOUBLE_XP]: '&#10024;',
      [BoxModifier.TRIPLE_XP]: '&#128142;',
      [BoxModifier.FREE_PASS]: '&#127915;',
      [BoxModifier.EXTRA_TIME]: '&#9203;',
      [BoxModifier.SHIELD]: '&#128737;',
      // Bamboozle
      [BoxModifier.HALF_TIME]: '&#9200;',
      [BoxModifier.FREEZE]: '&#129398;',
      [BoxModifier.STEAL]: '&#128176;',
      [BoxModifier.MINUS_SCORE]: '&#128165;',
      [BoxModifier.REVERSE]: '&#128260;'
    };
    return icons[modifier] || '&#63;';
  }

  getModifierColor(modifier: BoxModifier): string {
    const colors: Record<string, string> = {
      // Bonus - warm golds and greens
      [BoxModifier.DOUBLE_XP]: '#FFD700',
      [BoxModifier.TRIPLE_XP]: '#FF8F00',
      [BoxModifier.FREE_PASS]: '#4CAF50',
      [BoxModifier.EXTRA_TIME]: '#00BCD4',
      [BoxModifier.SHIELD]: '#66BB6A',
      // Bamboozle - reds and dark tones
      [BoxModifier.HALF_TIME]: '#FF5722',
      [BoxModifier.FREEZE]: '#9C27B0',
      [BoxModifier.STEAL]: '#F44336',
      [BoxModifier.MINUS_SCORE]: '#D32F2F',
      [BoxModifier.REVERSE]: '#E91E63'
    };
    return colors[modifier] || '#607D8B';
  }

  getCategoryLabel(): string {
    if (!this.state?.currentModifier) return '';
    const cat = this.getModifierCategory(this.state.currentModifier);
    if (cat === 'bonus') return this.currLang === 'ar' ? 'بطاقة مكافأة!' : 'BONUS CARD!';
    return this.currLang === 'ar' ? 'بطاقة مقلب!' : 'BAMBOOZLE!';
  }

  quitGame(): void {
    this.miniGameService.finishMiniGame();
  }

  private assignNextStudent(): void {
    if (!this.session) return;
    const students = this.session.students;
    if (!students.length) return;
    this.currentStudent = students[this.studentIndex % students.length];
    this.studentIndex++;
    this.miniGameService.setCurrentStudent(this.currentStudent.id);
  }

  getCurrentStudentName(): string {
    if (!this.currentStudent) return '';
    return this.currLang === 'ar' ? this.currentStudent.name.ar : this.currentStudent.name.en;
  }
}
